import * as React from 'react';

import { toMoney } from '@rematter/common/money';
import { enumInvoiceStatus } from '@rematter/frontend-lib-api-client';
import {
  Button,
  FooterItem,
  humanReadableNumber,
  Money as MoneyField,
  Row,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $currentDraftInvoiceInfo,
  $discardAllDrafts,
  $effectiveLineItems,
  $remoteInvoiceDetail,
  $updateInvoice,
} from '../../_state/invoiceDetail.state';
import { useTranslations } from '../../i18n/useTranslations';
import { InvoiceRecord } from '../../types';

import { toMoneyInput } from '~/modules/trade/utils/money';
import { getLocale } from '~/shared/utils/locale';

//
// ⚛️ ⚛️ ⚛️ INVOICE DETAIL FOOTER ⚛️ ⚛️ ⚛️
//
export const InvoiceDetailFooter = () => {
  // Translations
  const t = useTranslations();
  const toast = useToast();

  // Atoms
  const queryResult = useAtomValue($remoteInvoiceDetail);
  const invoice = (
    queryResult?.data as { invoice: InvoiceRecord | null } | undefined
  )?.invoice;
  const effectiveLineItems = useAtomValue($effectiveLineItems);
  const draftInvoiceInfo = useAtomValue($currentDraftInvoiceInfo);
  const updateInvoice = useSetAtom($updateInvoice);
  const discardAllDrafts = useSetAtom($discardAllDrafts);

  // State
  const [isSaving, setIsSaving] = React.useState(false);

  const isDisabled = React.useMemo(
    () =>
      invoice?.status === enumInvoiceStatus.Paid ||
      invoice?.status === enumInvoiceStatus.Voided,
    [invoice?.status],
  );

  const totalPaid = React.useMemo(() => {
    if (!invoice?.amount || !invoice?.openAmount) return null;
    return toMoney({
      amount: invoice.amount.amount - invoice.openAmount.amount,
      currencyCode: invoice.amount.currencyCode,
    });
  }, [invoice?.amount, invoice?.openAmount]);

  // Callbacks
  const handleSave = React.useCallback(async () => {
    if (!invoice?.id || isSaving) return;

    setIsSaving(true);
    try {
      const lineItemInputs = effectiveLineItems
        .filter((item) => item.description.trim() !== '')
        .map((item) => ({
          id: item.id.startsWith('temp-') ? undefined : item.id,
          description: item.description,
          quantity: item.quantity ?? 0,
          unit: item.unit ?? '',
          unitPrice: toMoneyInput(item.unitPrice),
          totalPrice: toMoneyInput(item.totalPrice),
          itemId: item.itemId ?? undefined,
        }));

      await updateInvoice({
        id: invoice.id,
        documentDate:
          draftInvoiceInfo?.documentDate ?? invoice.documentDate ?? undefined,
        documentNumber:
          draftInvoiceInfo?.documentNumber ??
          invoice.documentNumber ??
          undefined,
        dueDate: draftInvoiceInfo?.dueDate ?? invoice.dueDate ?? undefined,
        paymentTerm:
          draftInvoiceInfo?.paymentTerm ?? invoice.paymentTerm ?? undefined,
        notes: draftInvoiceInfo?.notes ?? invoice.notes ?? undefined,
        amount: draftInvoiceInfo?.amount
          ? toMoneyInput(draftInvoiceInfo.amount)
          : invoice.amount
            ? toMoneyInput(invoice.amount)
            : undefined,
        facilityId:
          draftInvoiceInfo?.facilityId ?? invoice.facilityId ?? undefined,
        lineItems: lineItemInputs,
      });

      toast.success(t.messages.invoices.updateSuccess);
    } catch (err) {
      toast.error(
        t.invoices.detail.messages.updateError(
          err instanceof Error ? err.message : t.messages.common.unknownError,
        ),
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    invoice,
    isSaving,
    effectiveLineItems,
    draftInvoiceInfo,
    updateInvoice,
    toast,
    t,
  ]);

  const handleDiscard = React.useCallback(() => {
    discardAllDrafts();
  }, [discardAllDrafts]);

  if (!invoice) return null;

  return (
    <Row xEnd yCenter gap5 className="@InvoiceDetailFooter">
      <Row xEnd yCenter gap8 className="[&>*]:min-h-auto">
        <FooterItem label={t.invoices.detail.footer.totalAmount}>
          <MoneyField
            className="text-lg leading-none font-bold"
            {...invoice.amount}
            locale={getLocale()}
          />
        </FooterItem>
        <FooterItem label={t.invoices.detail.footer.totalPaid}>
          <MoneyField
            className="text-lg leading-none font-bold"
            {...totalPaid}
            locale={getLocale()}
          />
        </FooterItem>
        <FooterItem label={t.invoices.detail.footer.numberOfPayments}>
          <span className="text-lg leading-none font-bold">
            {humanReadableNumber(invoice.payments?.length ?? 0, {
              locale: getLocale(),
              decimalPlaces: 0,
            })}
          </span>
        </FooterItem>
        <FooterItem
          className="text-lg leading-none font-bold"
          label={t.invoices.detail.footer.openAmount}
        >
          <MoneyField {...invoice.openAmount} locale={getLocale()} />
        </FooterItem>
      </Row>
      {!isDisabled && (
        <Row yCenter gap3 className="flex-shrink-0">
          <Button
            color="neutral"
            variant="outline"
            onPress={handleDiscard}
            isDisabled={isSaving}
          >
            {t.invoices.detail.unsavedChanges.discard}
          </Button>
          <Button
            color="primary"
            variant="solid"
            onPress={handleSave}
            isPending={isSaving}
            isLoading={isSaving}
          >
            {t.invoices.detail.unsavedChanges.save}
          </Button>
        </Row>
      )}
    </Row>
  );
};
